import React, { useEffect, useRef, useState } from "react";
import { MapPin, Navigation, Compass, Layers, AlertCircle } from "lucide-react";

export default function MapPreview({
  latitude,
  longitude,
  radius = 100,
  userLocation = null,
  siteName = "Site Location",
  height = 360,
  onLocationSelect = null,
  disabled = false,
}) {
  const mapContainerRef = useRef(null);
  const mapInstanceRef = useRef(null);
  const markerGroupRef = useRef(null);
  const onLocationSelectRef = useRef(onLocationSelect);
  const [mapLoaded, setMapLoaded] = useState(false);
  const [loadError, setLoadError] = useState(false);

  useEffect(() => {
    onLocationSelectRef.current = onLocationSelect;
  }, [onLocationSelect]);

  const hasCoordinates =
    latitude !== null &&
    latitude !== undefined &&
    latitude !== "" &&
    longitude !== null &&
    longitude !== undefined &&
    longitude !== "" &&
    !isNaN(Number(latitude)) &&
    !isNaN(Number(longitude));

  const latNum = hasCoordinates ? Number(latitude) : null;
  const lonNum = hasCoordinates ? Number(longitude) : null;
  const radiusNum = Number(radius) || 100;

  // Dynamically load Leaflet for real OpenStreetMap interactive rendering
  useEffect(() => {
    let isMounted = true;

    function initLeaflet() {
      if (!isMounted || !mapContainerRef.current) return;

      try {
        const L = window.L;
        if (!L) return;

        if (!mapInstanceRef.current && mapContainerRef.current) {
          const initialLat = hasCoordinates ? latNum : 19.185;
          const initialLon = hasCoordinates ? lonNum : 72.845;

          const map = L.map(mapContainerRef.current, {
            center: [initialLat, initialLon],
            zoom: 17,
            zoomControl: true,
            attributionControl: false,
          });

          L.tileLayer("https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png", {
            maxZoom: 19,
            subdomains: ["a", "b", "c"],
          }).addTo(map);

          // Click on map to select/adjust coordinates directly
          map.on("click", (e) => {
            if (onLocationSelectRef.current) {
              onLocationSelectRef.current({
                latitude: e.latlng.lat,
                longitude: e.latlng.lng,
              });
            }
          });

          const group = L.featureGroup().addTo(map);
          markerGroupRef.current = group;
          mapInstanceRef.current = map;
          setMapLoaded(true);
        }
      } catch (err) {
        console.warn("Leaflet initialization warning:", err);
        setLoadError(true);
      }
    }

    if (window.L) {
      initLeaflet();
    } else {
      // Inject CSS
      if (!document.getElementById("leaflet-css")) {
        const link = document.createElement("link");
        link.id = "leaflet-css";
        link.rel = "stylesheet";
        link.href = "https://unpkg.com/leaflet@1.9.4/dist/leaflet.css";
        document.head.appendChild(link);
      }

      // Inject JS
      if (!document.getElementById("leaflet-js")) {
        const script = document.createElement("script");
        script.id = "leaflet-js";
        script.src = "https://unpkg.com/leaflet@1.9.4/dist/leaflet.js";
        script.onload = () => {
          if (isMounted) initLeaflet();
        };
        script.onerror = () => {
          if (isMounted) setLoadError(true);
        };
        document.head.appendChild(script);
      } else {
        const script = document.getElementById("leaflet-js");
        script.addEventListener("load", () => {
          if (isMounted) initLeaflet();
        });
      }
    }

    return () => {
      isMounted = false;
      if (mapInstanceRef.current) {
        try {
          mapInstanceRef.current.remove();
        } catch {}
        mapInstanceRef.current = null;
        markerGroupRef.current = null;
      }
    };
  }, []);

  // Update markers and circle overlay whenever coordinates/radius change
  useEffect(() => {
    const L = window.L;
    const map = mapInstanceRef.current;
    const group = markerGroupRef.current;

    if (!L || !map || !group) return;

    group.clearLayers();

    if (hasCoordinates) {
      // 1. Center site marker (Orange Pin)
      const siteIcon = L.divIcon({
        className: "custom-site-pin",
        html: `
          <div style="display:flex;align-items:center;justify-content:center;width:34px;height:34px;background:#ea580c;color:white;border-radius:50%;border:3px solid white;box-shadow:0 3px 8px rgba(0,0,0,0.35);">
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round">
              <path d="M20 10c0 6-8 12-8 12s-8-6-8-12a8 8 0 0 1 16 0Z"/>
              <circle cx="12" cy="10" r="3"/>
            </svg>
          </div>
        `,
        iconSize: [34, 34],
        iconAnchor: [17, 34],
      });

      const siteMarker = L.marker([latNum, lonNum], { icon: siteIcon })
        .bindPopup(`<b>${siteName}</b><br/>Lat: ${latNum.toFixed(6)}<br/>Lon: ${lonNum.toFixed(6)}<br/>Radius: ${radiusNum}m`)
        .addTo(group);

      // 2. 100m Geofence Radius Circle (Orange border, translucent fill)
      const circle = L.circle([latNum, lonNum], {
        radius: radiusNum,
        color: "#ea580c",
        weight: 2,
        dashArray: "6, 6",
        fillColor: "#f97316",
        fillOpacity: 0.18,
      }).addTo(group);

      // 3. User current captured GPS marker if available (Blue Pin)
      if (
        userLocation &&
        userLocation.latitude !== null &&
        userLocation.longitude !== null &&
        !isNaN(Number(userLocation.latitude))
      ) {
        const uLat = Number(userLocation.latitude);
        const uLon = Number(userLocation.longitude);
        const uAcc = Number(userLocation.accuracy) || 0;

        const userIcon = L.divIcon({
          className: "custom-user-pin",
          html: `
            <div style="display:flex;align-items:center;justify-content:center;width:28px;height:28px;background:#2563eb;color:white;border-radius:50%;border:2px solid white;box-shadow:0 2px 6px rgba(0,0,0,0.3);">
              <div style="width:10px;height:10px;background:white;border-radius:50%;"></div>
            </div>
          `,
          iconSize: [28, 28],
          iconAnchor: [14, 14],
        });

        L.marker([uLat, uLon], { icon: userIcon })
          .bindPopup(`<b>Your Captured Location</b><br/>Accuracy: ±${Math.round(uAcc)}m`)
          .addTo(group);

        if (uAcc > 0 && uAcc <= 200) {
          L.circle([uLat, uLon], {
            radius: uAcc,
            color: "#3b82f6",
            weight: 1,
            fillColor: "#60a5fa",
            fillOpacity: 0.1,
          }).addTo(group);
        }
      }

      // Pan smoothly to site center or fit bounds
      try {
        map.setView([latNum, lonNum], 17);
      } catch {}
    }
  }, [hasCoordinates, latNum, lonNum, radiusNum, userLocation, siteName]);

  return (
    <div className="relative rounded-2xl overflow-hidden border border-gray-200 shadow-sm bg-gray-100">
      {/* Real Map Container */}
      <div
        ref={mapContainerRef}
        style={{ height: `${height}px`, width: "100%", zIndex: 1 }}
      />

      {/* Placeholder overlay when Geo-Fencing is disabled */}
      {disabled && (
        <div className="absolute inset-0 bg-gray-50/95 backdrop-blur-sm z-10 flex flex-col items-center justify-center p-6 text-center">
          <div className="w-16 h-16 rounded-2xl bg-rose-100 border border-rose-200 text-rose-600 flex items-center justify-center mb-3 shadow-inner">
            <AlertCircle className="w-8 h-8" />
          </div>
          <h4 className="font-bold text-gray-800 text-base">Geo-Fencing Disabled</h4>
          <p className="text-xs sm:text-sm text-gray-500 max-w-sm mt-1">
            Geo-Fencing is currently disabled for <b>{siteName}</b>. Enable status to configure site boundary coordinates.
          </p>
        </div>
      )}

      {/* Placeholder overlay when coordinates are not yet set */}
      {!disabled && !hasCoordinates && (
        <div className="absolute inset-0 bg-gray-50/90 backdrop-blur-sm z-10 flex flex-col items-center justify-center p-6 text-center">
          <div className="w-16 h-16 rounded-2xl bg-orange-100 border border-orange-200 text-orange-600 flex items-center justify-center mb-3 shadow-inner">
            <Compass className="w-8 h-8 animate-pulse" />
          </div>
          <h4 className="font-bold text-gray-800 text-base">Location Not Configured</h4>
          <p className="text-xs sm:text-sm text-gray-500 max-w-sm mt-1">
            Map preview will appear once you click <b>"Use My Current Location"</b> or manually enter latitude and longitude.
          </p>
        </div>
      )}

      {/* Floating Info Overlay Chip */}
      {hasCoordinates && (
        <div className="absolute top-3 left-3 z-20 bg-white/95 backdrop-blur-md px-3.5 py-2 rounded-xl border border-gray-200/80 shadow-md text-xs space-y-1">
          <div className="font-semibold text-gray-900 flex items-center gap-1.5">
            <MapPin className="w-3.5 h-3.5 text-orange-600" />
            <span>{siteName}</span>
          </div>
          <div className="text-gray-600 font-mono text-[11px] flex items-center gap-2">
            <span>{latNum?.toFixed(6)}, {lonNum?.toFixed(6)}</span>
            <span className="px-1.5 py-0.5 rounded bg-orange-100 text-orange-700 font-sans font-medium text-[10px]">
              {radiusNum}m radius
            </span>
          </div>
          {userLocation?.accuracy && (
            <div className="text-blue-600 text-[11px] font-medium flex items-center gap-1">
              <Navigation className="w-3 h-3" />
              <span>GPS Accuracy: ±{Math.round(userLocation.accuracy)}m</span>
            </div>
          )}
        </div>
      )}

      {/* Interactive indicator chip */}
      {onLocationSelect && (
        <div className="absolute top-3 right-3 z-20 bg-white/90 backdrop-blur-md px-2.5 py-1 rounded-lg border border-gray-200 shadow-sm text-[11px] text-gray-600 font-medium flex items-center gap-1.5 pointer-events-none">
          <span className="w-1.5 h-1.5 rounded-full bg-orange-500 animate-pulse"></span>
          <span>Click map to set pin</span>
        </div>
      )}

      {/* Legend Badge Bottom Right */}
      {hasCoordinates && (
        <div className="absolute bottom-3 right-3 z-20 bg-white/90 backdrop-blur px-2.5 py-1.5 rounded-lg border border-gray-200 text-[11px] text-gray-600 flex items-center gap-3 shadow-sm">
          <div className="flex items-center gap-1">
            <span className="w-2.5 h-2.5 rounded-full bg-orange-500 inline-block"></span>
            <span>Site Center</span>
          </div>
          <div className="flex items-center gap-1">
            <span className="w-2.5 h-2.5 rounded-full border border-orange-500 border-dashed inline-block"></span>
            <span>{radiusNum}m GeoFence</span>
          </div>
          {userLocation && (
            <div className="flex items-center gap-1">
              <span className="w-2.5 h-2.5 rounded-full bg-blue-600 inline-block"></span>
              <span>Your GPS</span>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
